import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { accountSchema, type AccountInput } from '@auditwire/contracts';
import { Button, Field, Input } from '@/shared/ui';
import { toMessage } from '@/shared/lib/repository/errors';
import { useAuth } from '@/features/auth';
import { useUpdateAccount } from '../api/useProfile';

export function AccountForm() {
  const { user } = useAuth();
  const updateAccount = useUpdateAccount();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AccountInput>({
    resolver: zodResolver(accountSchema),
    defaultValues: { email: user?.email ?? '', currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    const changes: { email?: string; password?: string } = {};
    if (values.email && values.email !== user?.email) changes.email = values.email;
    if (values.newPassword) changes.password = values.newPassword;

    if (!changes.email && !changes.password) {
      toast.info('Nothing to update');
      return;
    }

    try {
      await updateAccount.mutateAsync(changes);
      toast.success(changes.email ? 'Check your email to confirm the new address' : 'Password updated');
      reset({ email: values.email, currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(toMessage(err, 'Could not update your account'));
    }
  });

  return (
    <form onSubmit={onSubmit} className="space-y-5" noValidate>
      <Field label="Email" htmlFor="account-email" error={errors.email?.message}>
        <Input id="account-email" type="email" autoComplete="email" {...register('email')} />
      </Field>

      <div className="border-t border-border pt-5">
        <p className="mb-3 text-sm font-medium text-foreground">Change password</p>
        <div className="space-y-4">
          <Field label="New password" htmlFor="account-new" error={errors.newPassword?.message}>
            <Input
              id="account-new"
              type="password"
              autoComplete="new-password"
              placeholder="Leave blank to keep current"
              {...register('newPassword')}
            />
          </Field>
          <Field label="Confirm new password" htmlFor="account-confirm" error={errors.confirmPassword?.message}>
            <Input
              id="account-confirm"
              type="password"
              autoComplete="new-password"
              {...register('confirmPassword')}
            />
          </Field>
        </div>
      </div>

      <Button type="submit" disabled={updateAccount.isPending}>
        {updateAccount.isPending ? 'Saving…' : 'Update account'}
      </Button>
    </form>
  );
}
